import { Component, forwardRef, Input, OnChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  NgbDate,
  NgbDatepickerConfig,
  NgbDateStruct,
  NgbInputDatepicker,
  NgbTimepicker, NgbTooltip
} from '@ng-bootstrap/ng-bootstrap';
import { IonicModule } from '@ionic/angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgbTime } from '@ng-bootstrap/ng-bootstrap/timepicker/ngb-time';

import { default as dayjs } from 'dayjs';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { TranslateModule } from '@ngx-translate/core';
import { IoniconsOutline } from '@pt/pt-models';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const PLACEHOLDER_DATE = 'dd/MM/yyyy';
const PLACEHOLDER_DATETIME = 'dd/MM/yyyy HH:mm';

const DAYJS_FORMAT_DATE = 'DD/MM/YYYY';
const DAYJS_FORMAT_DATETIME = 'DD/MM/YYYY HH:mm';

export function dateTimeStringValidator(enableTime: boolean, isRequired: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!isRequired && (!control.value || control.value.length === 0)) {
      return null;
    }
    if (control.value) {
      if (enableTime) {
        return dayjs(control.value, DAYJS_FORMAT_DATETIME, true).isValid() ? null : { invalid: true };
      } else {
        return dayjs(control.value, DAYJS_FORMAT_DATE, true).isValid() ? null : { invalid: true };
      }
    }
    return { invalid: true };
  };
}

@UntilDestroy()
@Component({
  selector: 'pt-input-timestamp',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbInputDatepicker,
    IonicModule,
    NgbTimepicker,
    ReactiveFormsModule,
    NgbTooltip,
    TranslateModule
  ],
  templateUrl: `input-timestamp.component.html`,
  styleUrls: ['input-timestamp.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputTimestampComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputTimestampComponent),
      multi: true
    }
  ]
})

export class InputTimestampComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() label?: string;
  @Input() showTimePicker = true;
  @Input() isRequired = false;
  @Input() minDate: NgbDateStruct = this.s.minDate;

  @Input() tooltipInfo?: string;

  public placeholder = PLACEHOLDER_DATETIME;
  public dayJsFormatDate = DAYJS_FORMAT_DATETIME;
  IconInfoCircle = IoniconsOutline.infoCircle;

  // @ts-ignore
  private onChanged: (val) => void;
  // @ts-ignore
  private onTouched: () => void;

  public isDisabled = false;

  public dateFormGroup: FormGroup = new FormGroup({
    date: new FormControl(null, this.isRequired ? [Validators.required] : []),
    time: new FormControl({ hour: 0, minute: 0, second: 0 }),
    dateTimeToShow: new FormControl(null, [dateTimeStringValidator(this.showTimePicker, this.isRequired)])
  });


  constructor(private s: NgbDatepickerConfig) {

  }
  private generateStringTime(time: NgbTime | null): string {
    if (time && Number.isSafeInteger(time.hour) && Number.isSafeInteger(time.minute)) {
      return time.hour.toString().padStart(2, '0') + ':' + time.minute.toString().padStart(2, '0');
    }
    return '00:00';
  }

  private generateStringDate(date?: NgbDate | null): string | null {
    if (date && Number.isSafeInteger(date.day) && Number.isSafeInteger(date.month) && Number.isSafeInteger(date.year)) {
      return date.day.toString().padStart(2, '0') + '/' + date.month.toString().padStart(2, '0') + '/' + date.year;
    }
    return null;
  }

  ngOnChanges(): void {

    this.dateFormGroup.controls['date'].clearValidators();
    if (this.isRequired) {
      this.dateFormGroup.controls['date'].setValidators([Validators.required]);
    }
    this.dateFormGroup.controls['date'].updateValueAndValidity();

    if (this.showTimePicker) {
      this.placeholder = PLACEHOLDER_DATETIME;
      this.dayJsFormatDate = DAYJS_FORMAT_DATETIME;
      this.dateFormGroup.controls['dateTimeToShow'].setValidators([dateTimeStringValidator(this.showTimePicker, this.isRequired)]);
    } else {
      this.placeholder = PLACEHOLDER_DATE;
      this.dayJsFormatDate = DAYJS_FORMAT_DATE;
      this.dateFormGroup.controls['dateTimeToShow'].setValidators([dateTimeStringValidator(this.showTimePicker, this.isRequired)]);
    }
    const stringDate = this.generateStringDate(this.dateFormGroup.controls['date'].value);
    const stringTime = this.generateStringTime(this.dateFormGroup.controls['time'].value);
    this.alignStringDate(stringDate, stringTime);
    this.dateFormGroup.controls['dateTimeToShow'].updateValueAndValidity();
    this.dateFormGroup.updateValueAndValidity();
    this.dateFormGroup.markAsDirty();
  }

  ngOnInit(): void {
    /**
     * Questo evento si genera SOLO se si clicca sul calendario sezione DATE
     */
    this.dateFormGroup.controls['date'].valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((date?: NgbDate) => {
      const stringDate = this.generateStringDate(date);
      const stringTime = this.generateStringTime(this.dateFormGroup.controls['time'].value);
      if (date && stringDate && dayjs(stringDate, DAYJS_FORMAT_DATE, true).isValid()) {
        this.alignStringDate(stringDate, stringTime);
        this.propagateValidDate();
      } else {
        this.propagateInvalidDate();
      }
    });

    /**
     * Questo evento si genera SOLO se si clicca sul calendario sezione TIME
     */
    this.dateFormGroup.controls['time'].valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(time => {
      const stringDate = this.generateStringDate(this.dateFormGroup.controls['date'].value);
      const stringTime = this.generateStringTime(time);

      if (time && dayjs(stringDate, DAYJS_FORMAT_DATE, true).isValid()) {
        this.alignStringDate(stringDate, stringTime);
        this.propagateValidDate();
      } else {
        this.propagateInvalidDate();
      }
    });

    /**
     * Questo evento si genera SOLO se si clicca edita il calendario
     */
    this.dateFormGroup.controls['dateTimeToShow'].valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(dateTime => {
      if (dateTime && dayjs(dateTime, this.dayJsFormatDate, true).isValid()) {
        const date = dateTime.split(' ')[0];
        const time = this.showTimePicker ? dateTime.split(' ')[1] : '00:00';

        this.alignDate(date);
        this.alignTime(time);
        this.propagateValidDate();
      } else {
        this.propagateInvalidDate();
      }
    });
  }

  private propagateInvalidDate(): void {
    if (this.onChanged) {
      this.onChanged(null);
    }
    if (this.onTouched) {
      this.onTouched();
    }
  }

  private propagateValidDate(): void {
    let propagateValue: number | null = null;
    const stringDate = this.dateFormGroup.controls['dateTimeToShow'].value;
    const day = dayjs(stringDate, this.dayJsFormatDate, true);
    day.utc();

    if (stringDate && day.isValid() && day.toDate()) {
      propagateValue = day.toDate().getTime();
    }

    if (this.onChanged) {
      this.onChanged(propagateValue);
    }
    if (this.onTouched) {
      this.onTouched();
    }
  }

  writeValue(timestamp?: number): void {
    let writeValidDate = false;
    if (timestamp && !isNaN(timestamp) && isFinite(timestamp)) {
      const date = new Date(Number.parseInt(timestamp.toString()));
      if (date) {
        const ngbDate: NgbDate = new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
        const stringDate = this.generateStringDate(ngbDate);
        const stringTime = this.generateStringTime({
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: 0
        } as NgbTime);
        this.dateFormGroup.setValue({
          date: ngbDate,
          time: { hour: date.getHours(), minute: date.getMinutes(), second: 0 },
          dateTimeToShow: null
        }, { emitEvent: false });
        this.alignStringDate(stringDate, stringTime);
        writeValidDate = true;
      }
    }
    if (!writeValidDate) {
      this.dateFormGroup.setValue({
        date: null,
        time: { hour: 0, minute: 0, second: 0 },
        dateTimeToShow: null
      }, { emitEvent: false });
    }
    this.dateFormGroup.markAsPristine();
    this.dateFormGroup.markAllAsTouched();
  }


  onCalendarIcon($event: NgbInputDatepicker) {
    if (this.dateFormGroup.enabled) {
      $event.toggle();
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.isRequired && this.dateFormGroup.invalid) {
      return { invalid: true };
    }
    return null;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateFormGroup.disable({ emitEvent: false });
    } else {
      this.dateFormGroup.enable({ emitEvent: false });
    }
  }

  private alignStringDate(stringDate?: string | null, stringTime?: string | null) {
    if (this.showTimePicker) {
      if (stringDate && stringTime) {
        this.dateFormGroup.controls['dateTimeToShow'].setValue(stringDate + ' ' + stringTime, { emitEvent: false });
      } else {
        this.dateFormGroup.controls['dateTimeToShow'].setValue(null, { emitEvent: false });
      }

    } else {

      if (stringDate) {
        this.dateFormGroup.controls['dateTimeToShow'].setValue(stringDate, { emitEvent: false });
      } else {
        this.dateFormGroup.controls['dateTimeToShow'].setValue(null, { emitEvent: false });
      }

    }
  }

  /**
   * DD/MM/YYYY
   * @param date
   * @private
   */
  private alignDate(date?: string) {
    if (date && dayjs(date, DAYJS_FORMAT_DATE, true).isValid()) {
      const value: NgbDate =
        new NgbDate(
          Number.parseInt(date.split('/')[2]),
          Number.parseInt(date.split('/')[1]),
          Number.parseInt(date.split('/')[0]));
      this.dateFormGroup.controls['date'].setValue(value, { emitEvent: false });
    } else {
      this.dateFormGroup.controls['date'].setValue(null, { emitEvent: false });
    }
  }

  /**
   * HH:mm
   * @param time
   * @private
   */
  private alignTime(time?: string) {
    if (time) {
      const value = {
        hour: Number.parseInt(time.split(':')[0]),
        minute: Number.parseInt(time.split(':')[1]),
        second: 0
      };
      this.dateFormGroup.controls['time'].setValue(value, { emitEvent: false });
    } else {
      this.dateFormGroup.controls['time'].setValue({ hour: 0, minute: 0, second: 0 }, { emitEvent: false });
    }
  }
}


